// Import necessary functions and modules
import {
  handleStep1UploadDocuments,
  handleStep2ExtractText,
  handleStep3ReformatText,
  handleStep4ExtractDataPoints,
  handleStep4bCreateStructuredHTML,
  handleStep5UpdateFirestore,
  handleStep6ConsolidateDataPoints,
  handleStep7ResolveConflicts,
  handleStep8GenerateAuditRiskProfile,
} from "./InputFormFunctions"; // Adjust path if necessary

export const manageDocumentSteps = async (
  step, // Add step parameter
  documentStepMemory,
  updateDocumentStepMemory,
  setState,
  uploadedFiles,
  company,
  collectionBrand,
  systemName,
  uniqueId,
  userEmail,
  dataPointDefinitions,
  manageDocumentSteps,
  documentSupportData,
  totalDocuments
) => {
  try {
    switch (step.current) {
      case "step1": // Upload Documents CHECKED
        /**
         * --------------------------------STEP 1---------------------------------
         *
         * Example Object to be produced in `uploadResults`:
         *
         * @example
         * {
         *   "proofOfBankAccount": {
         *     filePath: "AndUs/FinanceSystem/SupplierData/abc123/proofOfBankAccount.pdf",
         *     downloadURL: "https://firebasestorage.googleapis.com/.../proofOfBankAccount.pdf"
         *   },
         *   "ptyRegistration": {
         *     filePath: "AndUs/FinanceSystem/SupplierData/abc123/ptyRegistration.pdf",
         *     downloadURL: "https://firebasestorage.googleapis.com/.../ptyRegistration.pdf"
         *   },
         *   "beeCertificate": {
         *     filePath: "AndUs/FinanceSystem/SupplierData/abc123/beeCertificate.pdf",
         *     downloadURL: "https://firebasestorage.googleapis.com/.../beeCertificate.pdf"
         *   },
         *   "proofOfAddress": {
         *     filePath: "AndUs/FinanceSystem/SupplierData/abc123/proofOfAddress.pdf",
         *     downloadURL: "https://firebasestorage.googleapis.com/.../proofOfAddress.pdf"
         *   }
         * }
         * Call Step 1: c
         */
        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Update previous to the current step
            current: "processing", // Set current to processing while uploading
          },
          loadingLabel: "Uploading documents...", // Update loading label
        }));

        try {
          console.log("-----STEP 1-----");
          console.log("Document support data", documentSupportData);
          // Perform the document upload operation
          const uploadResults = await handleStep1UploadDocuments(
            uploadedFiles,
            company,
            collectionBrand,
            systemName,
            uniqueId,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            totalDocuments
          );

          // Update memory with step results
          updateDocumentStepMemory({
            ...documentStepMemory,
            uploadResults,
          });

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step
              current: "step2", // Move to the next step
            },
          }));
        } catch (error) {
          console.error("Error during Step 1 (Uploading Documents):", error);

          // Set error state and provide retry option for the entire document upload process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while uploading documents. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set the previous to the current step ("step1")
              current: "uploadError", // Set current to "uploadError" to indicate the retry option
            },
          }));
        }
        break;

      case "step2": // Extracting text from uploaded documents CHECKED
        /**
         * --------------------------------STEP 2---------------------------------
         * Extracting text from uploaded documents.
         *
         * Example Object to be produced in `textExtractionResults`:
         * DocumentStepMemory.textExtractionResults
         *
         *@example
         * {
         *   "proofOfBankAccount": {
         *     "extractedText": "Extracted text from proofOfBankAccount document.",
         *     "isOCR": true // Whether OCR was used to extract the text
         *   },
         *   "ptyRegistration": {
         *     "extractedText": "Extracted text from ptyRegistration document.",
         *     "isOCR": false
         *   },
         *   "beeCertificate": {
         *     "extractedText": "Extracted text from beeCertificate document.",
         *     "isOCR": false
         *   },
         *   "proofOfAddress": {
         *     "extractedText": "Extracted text from proofOfAddress document.",
         *     "isOCR": true
         *   }
         * }
         * Call Step 2: Extract Text from Uploaded Documents
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step2")
            current: "processing", // Set current to "processing" while extracting text
          },
          loadingLabel: "Extracting text from documents...", // Update loading label
        }));

        try {
          // Perform the text extraction operation
          const textExtractionResults = await handleStep2ExtractText(
            documentStepMemory.uploadResults,
            uploadedFiles,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            userEmail,
            systemName,
            company,
            collectionBrand,
            documentSupportData,
            totalDocuments
          );

          // Split the textExtractionResults into two separate objects
          const extractedTextResults = {};
          const isOCRdocument = {};

          for (const [documentName, { extractedText, isOCR }] of Object.entries(
            textExtractionResults
          )) {
            // Create the object with only the extracted text, similar to the previous version
            extractedTextResults[documentName] = extractedText;

            // Create a new object with the isOCR flag for each document
            isOCRdocument[documentName] = isOCR;
          }

          // Filter out documents with null extracted text values
          const filteredExtractedTextResults = {};
          for (const [documentName, extractedText] of Object.entries(
            extractedTextResults
          )) {
            if (extractedText !== null) {
              filteredExtractedTextResults[documentName] = extractedText;
            }
          }

          // Update memory with step results
          updateDocumentStepMemory({
            ...documentStepMemory,
            textExtractionResults: extractedTextResults,
            isOCRdocument,
          });

          console.log("--------------------------------------------------");
          console.log(documentStepMemory.textExtractionResults);
          console.log("--------------------------------------------------");

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step3", // Move to the next step ("step3")
            },
          }));
        } catch (error) {
          console.error("Error during Step 2 (Extracting Text):", error);

          // Set error state and provide retry option for the extraction process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message: "An error occurred while extracting text. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step2")
              current: "uploadError", // Set current to "uploadError" to indicate the retry option
            },
          }));
        }
        break;

      case "step3": // Reformatting extracted text using AI CHECKED
        /**
         * --------------------------------STEP 3---------------------------------
         * Reformatting extracted text using AI.
         *
         * Example Object to be produced in `reformattedTextResults`:
         * DocumentStepMemory.reformattedTextResults
         * @example
         * {
         *   "proofOfBankAccount": "Formatted text for proofOfBankAccount document.",
         *   "ptyRegistration": "Formatted text for ptyRegistration document.",
         *   "beeCertificate": "Formatted text for beeCertificate document.",
         *   "proofOfAddress": "Formatted text for proofOfAddress document."
         * }
         * Call Step 3: Reformat Extracted Text using AI
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step3")
            current: "processing", // Set current to "processing" while reformatting text
          },
          loadingLabel: "Reformatting extracted text using AI...", // Update loading label
        }));

        try {
          console.log("-----STEP 3-----");
          // Perform the reformatting operation
          const reformattedTextResults = await handleStep3ReformatText(
            documentStepMemory.textExtractionResults,
            documentStepMemory.isOCRdocument,
            userEmail,
            systemName,
            company,
            collectionBrand,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            documentSupportData,
            uploadedFiles,
            totalDocuments
          );

          // Update memory with step results
          updateDocumentStepMemory({
            ...documentStepMemory,
            reformattedTextResults,
          });

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step4", // Move to the next step ("step4")
            },
          }));
          console.log("--------------------------------------------------");
          console.log(documentStepMemory.reformattedTextResults);
          console.log("--------------------------------------------------");
        } catch (error) {
          console.error(
            "Error during Step 3 (Reformatting Extracted Text):",
            error
          );

          // Set error state and provide retry option for the reformatting process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while reformatting the extracted text. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step3")
              current: "uploadError", // Set current to "uploadError" to indicate the retry option
            },
          }));
        }
        break;

      case "step4": // Extracting data points from formatted text using AI. CHECKED
        /**
         * --------------------------------STEP 4---------------------------------
         * Extracting data points from formatted text using AI.
         *
         * Example Object to be produced in `extractedDataPointsResults`:
         * DocumentStepMemory.extractedDataPointsResults
         * @example
         * {
         *   "proofOfBankAccount": {
         *     "documentType": "Proof of Bank Account",
         *     "isproofOfBankAccount": true,
         *     "docOverview": "This document appears to be a valid proof of bank account for the company.",
         *     "riskScore": "Low",
         *     "riskElements": "None",
         *     "companyName": "BETTER BEST EDUCATIONAL PROJECTS (PTY) LTD",
         *     "accountNumber": "53510029333",
         *     "bankName": "First National Bank"
         *   },
         *   // Additional documents here...
         * }
         * Call Step 4: Extract Data Points from Formatted Text using AI
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step4")
            current: "processing", // Set current to "processing" while extracting data points
          },
          loadingLabel:
            "Extracting data points from formatted text using AI...", // Update loading label
        }));

        try {
          console.log("-----STEP 4a-----");
          // Extract data points from the reformatted text
          const extractedDataPointsResults = await handleStep4ExtractDataPoints(
            documentStepMemory.reformattedTextResults,
            dataPointDefinitions,
            userEmail,
            systemName,
            company,
            collectionBrand,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            documentSupportData,
            totalDocuments
          );

          // Update memory with step results
          updateDocumentStepMemory({
            ...documentStepMemory,
            extractedDataPointsResults,
          });

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step4b", // Move to the next step ("step4b")
            },
          }));
          console.log(
            "--------------------------extractedDataPointsResults------------------------"
          );
          console.log(documentStepMemory.extractedDataPointsResults);
          console.log("--------------------------------------------------");
        } catch (error) {
          console.error("Error during Step 4 (Extracting Data Points):", error);

          // Set error state and provide retry option for the data point extraction process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while extracting data points. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step4")
              current: "uploadError", // Set current to "uploadError" to allow retry
            },
          }));
        }
        break;

      case "step4b": // Create structured HTML for each document using AI. CHECKED
        /**
         * --------------------------------STEP 4b---------------------------------
         * Create structured HTML for each document using AI.
         *
         * Example Object to be produced in `structuredHTMLResults`:
         * DocumentStepMemory.structuredHTMLResults
         * @example
         * {
         *   "proofOfBankAccount": "<Document><header>Proof of Bank Account...</header>...</Document>",
         *   "ptyRegistration": "<Document><header>PTY Registration...</header>...</Document>",
         *   // Additional documents here...
         * }
         * Call Step 4b: Create Structured HTML using AI
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step4b")
            current: "processing", // Set current to "processing" while generating structured HTML
          },
          loadingLabel: "Creating structured HTML for each document...", // Update loading label
        }));

        try {
          console.log("-----STEP 4b-----");
          // Generate structured HTML for each document
          const structuredHTMLResults = await handleStep4bCreateStructuredHTML(
            documentStepMemory.extractedDataPointsResults,
            documentStepMemory.reformattedTextResults,
            documentStepMemory.isOCRdocument,
            userEmail,
            systemName,
            company,
            collectionBrand,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            documentSupportData,
            uploadedFiles,
            totalDocuments
          );

          // Update memory with structured HTML results
          updateDocumentStepMemory({
            ...documentStepMemory,
            structuredHTMLResults,
          });

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step5", // Move to the next step ("step5")
            },
          }));
          console.log(
            "--------------------------structuredHTMLResults------------------------"
          );
          console.log(documentStepMemory.structuredHTMLResults);
          console.log("--------------------------------------------------");
        } catch (error) {
          console.error(
            "Error during Step 4b (Creating Structured HTML):",
            error
          );

          // Set error state and provide retry option for the structured HTML generation process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while creating structured HTML. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step4b")
              current: "uploadError", // Set current to "uploadError" to allow retry
            },
          }));
        }
        break;

      case "step5": // Updating Firestore with the document details ASSUMED
        /**
         * --------------------------------STEP 5---------------------------------
         * Updating Firestore with the document details.
         *
         * Example Firestore data structure to be produced:
         * DocumentStepMemory.firestoreUpdateResults
         * @example
         * {
         *   "proofOfBankAccountDocument": {
         *     "url": "https://firebasestorage.googleapis.com/.../proofOfBankAccount.pdf",
         *     "electronicDocument": "Formatted text for proofOfBankAccount document.",
         *     "auditResult": {
         *       "documentType": "Proof of Bank Account",
         *       "isproofOfBankAccount": true,
         *       "docOverview": "This document appears to be a valid proof of bank account for the company.",
         *       "riskScore": "Low",
         *       "riskElements": "None",
         *       "companyName": "BETTER BEST EDUCATIONAL PROJECTS (PTY) LTD",
         *       "accountNumber": "53510029333",
         *       "bankName": "First National Bank"
         *     }
         *   },
         *   // Additional documents here...
         * }
         * Call Step 5: Update Firestore with document details
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step5")
            current: "processing", // Set current to "processing" while updating Firestore
          },
          loadingLabel: "Updating Firestore with document details...", // Update loading label
        }));

        try {
          console.log("-----STEP 5-----");
          // Perform Firestore update with document details
          await handleStep5UpdateFirestore(
            company,
            collectionBrand,
            systemName,
            uniqueId,
            documentStepMemory.uploadResults,
            documentStepMemory.reformattedTextResults,
            documentStepMemory.extractedDataPointsResults,
            documentStepMemory.structuredHTMLResults,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            documentStepMemory.isOCRdocument,
            totalDocuments
          );

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step6", // Move to the next step ("step6")
            },
          }));
        } catch (error) {
          console.error("Error during Step 5 (Updating Firestore):", error);

          // Set error state and provide retry option for the Firestore update process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while updating Firestore. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step5")
              current: "uploadError", // Set current to "uploadError" to allow retry
            },
          }));
        }
        break;

      case "step6": // Consolidate data points from multiple documents CHECKED
        /**
         * --------------------------------STEP 6---------------------------------
         * Consolidate data points from multiple documents.
         *
         * Example Object to be produced in `consolidatedDataPoints`:
         * @example
         * {
         *   "companyName": {
         *     value: "BETTER BEST EDUCATIONAL PROJECTS (PTY) LTD",
         *     documents: ["proofOfBankAccount", "ptyRegistration"],
         *   },
         *   "accountNumber": {
         *     value: "conflict",
         *     documents: [
         *       { value: "53510029333", sources: ["proofOfBankAccount"] },
         *       { value: "12345678900", sources: ["beeCertificate"] }
         *     ],
         *   }
         * }
         *
         * rejectedDocumentDatapoints: Datapoints extracted from a document that were rejected
         * Call Step 6: Consolidate Extracted Data Points
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step6")
            current: "processing", // Set current to "processing" while consolidating data points
          },
          loadingLabel: "Consolidating extracted data points...", // Update loading label
        }));

        try {
          console.log("-----STEP 6-----");
          // Call the function to consolidate data points from multiple documents
          const consolidatedDataPoints = await handleStep6ConsolidateDataPoints(
            documentStepMemory.extractedDataPointsResults,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            uploadedFiles,
            totalDocuments
          );

          // Update memory with consolidated data
          updateDocumentStepMemory({
            ...documentStepMemory,
            consolidatedDataPoints,
          });

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step7", // Move to the next step ("step7")
            },
          }));
          console.log(
            "--------------------------consolidatedDataPoints------------------------"
          );
          console.log(documentStepMemory.consolidatedDataPoints);
          console.log("--------------------------------------------------");
        } catch (error) {
          console.error(
            "Error during Step 6 (Consolidating Data Points):",
            error
          );

          // Set error state and provide retry option for the consolidation process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while consolidating data points. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step6")
              current: "uploadError", // Set current to "uploadError" to allow retry
            },
          }));
        }
        break;

      case "step7": // Resolve conflicting data points using AI. PROBLEM
        /**
         * --------------------------------STEP 7---------------------------------
         * Resolve conflicting data points using AI.
         *
         * Example Object to be produced in `resolvedDataPoints`:
         * DocumentStepMemory.resolvedDataPoints
         *
         * The object being produced here will be one of the two following options, depending on the level at which we could successfully extract the JSON:
         *
         * 1. Complex Structure:
         * @example
         * {
         *   "dataPoints": [
         *     {
         *       "fieldName": "fieldName1",
         *       "status": "resolved", // or "unresolved"
         *       "value": "resolved_value", // only for resolved data points
         *       "conflictingValues": [
         *         {
         *           "documentName": "Document A",
         *           "value": "Conflicting Value 1"
         *         },
         *         {
         *           "documentName": "Document B",
         *           "value": "Conflicting Value 2"
         *         }
         *       ] // Include only if there are conflicting values
         *     },
         *     ...
         *   ]
         * }
         *
         * 2. Simple Structure (Fallback if complex extraction fails):
         * @example
         * {
         *   "dataPoints": {
         *     "fieldName1": "resolved_value_or_empty_string",
         *     "fieldName2": "resolved_value_or_empty_string",
         *     ...
         *   }
         * }
         *
         * Call Step 7: Resolve Conflicting Data Points using AI
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step7")
            current: "processing", // Set current to "processing" while resolving data points
          },
          loadingLabel: "Resolving conflicting data points...", // Update loading label
        }));

        try {
          console.log("-----STEP 7-----");
          // Call the function to resolve conflicts using AI
          const resolvedDataPoints = await handleStep7ResolveConflicts(
            documentStepMemory.consolidatedDataPoints, // Consolidated data from Step 6
            systemName,
            userEmail,
            company,
            collectionBrand,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            documentSupportData,
            totalDocuments
          );

          console.log("[Step 7] resolvedConflictingDataPointsJson →", resolvedDataPoints);

          // Update memory with resolved data points
          updateDocumentStepMemory({
            ...documentStepMemory,
            resolvedDataPoints,
          });

          // Move to the next step upon successful completion
          setState((prevState) => ({
            ...prevState,
            step: {
              previous: prevState.step.current, // Set previous to the current step ("processing")
              current: "step8", // Move to the next step ("step8")
            },
          }));
          console.log(
            "--------------------------resolvedDataPoints------------------------"
          );
          console.log(documentStepMemory.resolvedDataPoints);
          console.log("--------------------------------------------------");
        } catch (error) {
          console.error(
            "Error during Step 7 (Resolving Data Conflicts):",
            error
          );

          // Set error state and provide retry option for the conflict resolution process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while resolving data conflicts. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step7")
              current: "uploadError", // Set current to "uploadError" to allow retry
            },
          }));
        }
        break;

      case "step8": // Updated to use the new `step` state approach
        /**
         * --------------------------------STEP 8---------------------------------
         * Generate Audit Risk Profile using AI.
         *
         * Example Object to be produced in `auditRiskProfile`:
         * DocumentStepMemory.auditRiskProfile
         * @example
         * {
         *   "auditStatus": "Approved",
         *   "auditOverview": "The documents provided are consistent and no high-risk issues were found.",
         *   "riskScore": "Low",
         *   "riskElements": ["None"]
         * }
         * Call Step 8: Generate Audit Risk Profile
         */

        // Set the current step to processing
        setState((prevState) => ({
          ...prevState,
          step: {
            previous: prevState.step.current, // Set previous to the current step ("step8")
            current: "processing", // Set current to "processing" while generating audit risk profile
          },
          loadingLabel: "Generating audit risk profile...", // Update loading label
        }));

        try {
          console.log("-----STEP 8-----");
          // Pre-process the resolvedDataPoints to remove unwanted fields
          const preprocessedResolvedDataPoints = Object.fromEntries(
            Object.entries(documentStepMemory.resolvedDataPoints).filter(
              ([key]) =>
                key !== "documentType" &&
                key !== "docOverview" &&
                key !== "riskScore" &&
                key !== "riskElements" &&
                key !== "status" &&
                !/^is\w+/i.test(key) // Exclude any fields that start with "is"
            )
          );

          // Pre-process the audit data to include only the required fields for each document
          const preprocessedAuditData = Object.fromEntries(
            Object.entries(documentStepMemory.extractedDataPointsResults).map(
              ([documentName, documentData]) => [
                documentName,
                {
                  documentType: documentData.documentType,
                  [`is${documentName}`]: documentData[`is${documentName}`],
                  docOverview: documentData.docOverview,
                  riskScore: documentData.riskScore,
                  riskElements: documentData.riskElements,
                },
              ]
            )
          );

          // Generate the audit risk profile
          const auditRiskProfile = await handleStep8GenerateAuditRiskProfile(
            preprocessedResolvedDataPoints, // Pre-processed resolved data from Step 7
            preprocessedAuditData, // Pre-processed audit data from extracted data points
            systemName,
            userEmail,
            company,
            collectionBrand,
            uniqueId,
            (label) =>
              setState((prevState) => ({
                ...prevState,
                loadingLabel: label,
              })),
            documentSupportData
          );

          // Update memory with step results
          updateDocumentStepMemory({
            ...documentStepMemory,
            auditRiskProfile,
          });
          console.log(
            "--------------------------auditRiskProfile------------------------"
          );
          console.log(documentStepMemory.auditRiskProfile);
          console.log("--------------------------------------------------");
        } catch (error) {
          console.error(
            "Error during Step 8 (Generating Audit Risk Profile):",
            error
          );

          // Set error state and provide retry option for the audit risk profile generation process
          setState((prevState) => ({
            ...prevState,
            result: {
              type: "retry",
              message:
                "An error occurred while generating the audit risk profile. Please retry.",
            },
            step: {
              previous: prevState.step.current, // Set previous to the current step ("step8")
              current: "uploadError", // Set current to "uploadError" to allow retry
            },
          }));
        }
        break;
    }
  } catch (error) {
    console.error("Unexpected error in manageDocumentSteps:", error);
    setState((prevState) => ({
      ...prevState,
      result: {
        type: "error",
        message: "An unexpected error occurred. Please retry.",
      },
      step: "uploadError",
    }));
  }
};